<template>
  <v-form ref="form" v-model="valid" class="pa-3">
    <div class="mb-10">
      <v-btn-toggle :value="donationTypeId" mandatory color="primary accent-3" group tile>
        <v-btn
          v-for="donationType in filteredDonationTypes"
          :key="donationType.id"
          x-large
          :value="donationType.type.id"
          @click="changeDonationType(donationType.type.id)"
        >
          {{ donationType.type.name }}
        </v-btn>
      </v-btn-toggle>
    </div>

    <div>
      <DonationSuggestions :suggestions="suggestedAmounts" :selected-amount="defaultSelectedAmount" :is-recurring-donation="isRecurringDonation" />
    </div>
    <div v-if="isRecurringDonation">
      <template v-if="!estDAS">
        <p>{{ t("LABEL_FREQUENCE") }}</p>
        <v-radio-group v-model="frequence" row :rules="requiredRules" @change="frequenceChanged">
          <v-radio v-for="f in frequences" :key="f.id" :label="f.name" :value="f.id" />
        </v-radio-group>
      </template>

      <v-text-field
        v-model="nombreVersements"
        type="number"
        min="0"
        step="1"
        :max="nombreVersementsMax"
        :label="t('LABEL_NOMBRE_VERSEMENTS')"
        :outlined="isOutlined"
        :dense="isDense"
        :rules="nombreVersementsRules"
        style="width: 250px"
        @change="persistToStore"
      ></v-text-field>

      <DatePicker
        v-if="donateurDefinitDateDebut"
        v-model="dateDebut"
        :label="t('LABEL_DATE_PREMIER_PRELEVEMENT')"
        :minValue="dateDebutMinimal"
      />

      <v-alert type="info" color="primary" outlined class="mt-4">
        <div>
          <h3 class="subtitle-1">
            Date de premier prélèvement: <span class="font-weight-bold primary--text">{{ dateDebut | longdate }}</span>
          </h3>
          <h3 class="subtitle-1">
            Date de fin de l'engagement: <span class="font-weight-bold primary--text">{{ dateFin | longdate }}</span>
          </h3>
          <h3 class="subtitle-1">
            Montant total de l'engagement: <span class="font-weight-bold primary--text">{{ montantTotal | money }}</span>
          </h3>
        </div>
      </v-alert>
    </div>
    <template v-if="projects.length > 0">
      <v-select
        v-show="projects.length > 1"
        id="projectInput"
        v-model="project"
        :items="projects"
        item-text="name"
        item-value="id"
        :label="t('LABEL_PROJET')"
        required
        :rules="requiredRules"
        :outlined="isOutlined"
        :dense="isDense"
        @change="persistToStore"
      />
    </template>

    <v-checkbox v-model="wantsToReceiveRF" :label="t('JE_VEUX_RECEVOIR_UN_RF')" />
    <DedicaceInfoForm v-if="!isRecurringDonation" />
    <div v-if="estPB">
      <v-select
        v-model="donFaitDeLaPartDe"
        :items="faitDeLaPartDe"
        item-text="name"
        item-value="id"
        :label="t('LABEL_DON_FAIT_DE_LA_PART_DE')"
        required
        :rules="requiredRules"
        :outlined="isOutlined"
        :dense="isDense"
        @change="persistToStore"
      />
      <v-text-field
        v-model="prenomEnfant"
        :label="t('LABEL_PRENOM_ENFANT')"
        :outlined="isOutlined"
        :dense="isDense"
        :rules="requiredRules"
        @blur="persistToStore"
      ></v-text-field>
      <v-text-field
        v-model="nomEnfant"
        :label="t('LABEL_NOM_ENFANT')"
        :outlined="isOutlined"
        :dense="isDense"
        :rules="requiredRules"
        @blur="persistToStore"
      ></v-text-field>
      <v-text-field
        v-model="matriculeEnfant"
        :label="t('LABEL_MATRICULE_ENFANT')"
        :outlined="isOutlined"
        :dense="isDense"
        :rules="requiredRules"
        @blur="persistToStore"
      ></v-text-field>
      <v-select
        v-model="statutEnfant"
        :items="statutsEnfant"
        item-text="name"
        item-value="id"
        :label="t('LABEL_STATUT_ENFANT')"
        required
        :rules="requiredRules"
        :outlined="isOutlined"
        :dense="isDense"
        @change="persistToStore"
      />
      <v-select
        v-model="programmeEtude"
        :items="programmesEtude"
        item-text="name"
        item-value="id"
        :label="t('LABEL_PROGRAMME_ETUDE')"
        required
        :rules="requiredRules"
        :outlined="isOutlined"
        :dense="isDense"
        @change="persistToStore"
      />
    </div>
    <div v-if="estVO">
      <v-select
        v-model="equipeVertEtOr"
        :items="equipes"
        item-text="name"
        item-value="id"
        :label="t('LABEL_EQUIPE_VERT_ET_OR')"
        required
        :rules="requiredRules"
        :outlined="isOutlined"
        :dense="isDense"
        @change="persistToStore"
      />
    </div>
    <FormActionBar :valid-label="t('VALIDER')" :can-validate="true" @valid-click="validate" />

    <v-alert v-if="error.length" type="error" text class="ma-2">
      {{ error }}
    </v-alert>
  </v-form>
</template>

<script>
import DonationSuggestions from "@/components/DonationSuggestions"
import { EventBus } from "@/main.js"
import FormActionBar from "@/components/FormActionBar"
import { mapActions, mapGetters } from "vuex"
import DedicaceInfoForm from "./DedicaceInfoForm.vue"
import DatesHelpers from "@/components/ui/mixins/DatesHelpers"
import DatePicker from './DatePicker.vue'

export default {
  components: {
    DonationSuggestions,
    FormActionBar,
    DedicaceInfoForm,
    DatePicker
  },
  mixins: [DatesHelpers],
  data: () => ({
    donationTypeId: undefined,
    amount: 0,
    project: null,
    error: "",
    wantsToReceiveRF: true,
    valid: false,
    frequence: undefined,
    nombreVersements: 60,
    nombreVersementsMax: 260,
    dateDebut: null,
    donFaitDeLaPartDe: undefined,
    prenomEnfant: "",
    nomEnfant: "",
    matriculeEnfant: "",
    statutEnfant: undefined,
    programmeEtude: undefined,
    equipeVertEtOr: undefined,
    infos: {
      serverDateTime: null,
    },
  }),
  computed: {
    ...mapGetters([
      "donationInfo",
      "donationTypes",
      "params",
      "isOutlined",
      "isDense",
      "t",
      "projects",
      "sortedDisplayDaysOfMonthPayment",
      "frequences",
      "faitDeLaPartDe",
      "statutsEnfant",
      "programmesEtude",
      "equipes"
    ]),
    nombreVersementsRules() {
      return [
        (v) => parseInt(v) > 0 || "Le nombre de versement doit être supérieur à 0.",
        (v) => parseInt(v) <= this.nombreVersementsMax || `Entrez un nombre inférieur ou égal à ${this.nombreVersementsMax}`
      ]
    },
    requiredRules() {
      return [(v) => !!v || this.t("CHAMP_REQUIS")]
    },
    defaultDonationType() {
      if (this.filteredDonationTypes.length > 0) {
        return this.filteredDonationTypes[0].type.id
      }
      return 0
    },
    isRecurringDonation() {
      let donationType = this.donationTypes.find((t) => t.type.id == this.donationTypeId)
      if (donationType) {
        return donationType.isRecurring
      }
      return false
    },
    defaultSelectedAmount() {
      return this.suggestedAmounts.find((s) => s.byDefault)
    },
    suggestedAmounts() {
      return this.params.suggestedAmounts.filter((a) => a.type === Number(this.donationTypeId)).sort((a, b) => a - b)
    },
    filteredDonationTypes() {
      const types = this.donationTypes
      if (!types) {
        return []
      }
      return types
        .filter((t) => !t.hidden)
        .sort((a, b) => {
          if (a.order > b.order) {
            return 1
          } else {
            return -1
          }
        })
    },
    dateFin() {
      const dateRef = this.parseLocalDate(this.dateDebut)

      if (this.estDAS) {
        const days = (parseInt(this.nombreVersements) - 1) * 14
        return this.addDays(dateRef, days)
      }

      if (this.frequence === this.params.frequenceMensuelleId) {
        return this.addMonths(dateRef, parseInt(this.nombreVersements) - 1)
      } else {
        return this.addYears(dateRef, parseInt(this.nombreVersements) - 1)
      }
    },
    montantTotal() {
      return this.amount * this.nombreVersements
    },
    estDAS() {
      return this.params.formParams.typeDeFormulaire === "DAS"
    },
    estPB() {
      return this.params.formParams.typeDeFormulaire === "PB"
    },
    estVO() {
      return this.params.formParams.typeDeFormulaire === "VO"
    },
    donateurDefinitDateDebut() {
      return this.params.formParams.donateurDefinitDateDebut
    },
    dateDebutMinimal() {
      return this.convertDateToString(new Date())
    },
  },
  watch: {
    valid() {
      if (this.valid) {
        this.$emit("validateStep")
      } else {
        this.$emit("invalidateStep")
      }
    }
  },
  mounted() {
    EventBus.$on("select-amount", this.changeAmount)
    this.donationTypeId = this.defaultDonationType
    this.setDefaultSelectedAmount()
    this.project = this.projects.length == 1 ? this.projects[0].id : null
    this.dateDebut = this.estDAS ? this.convertDateToString(new Date(this.params.dateProchainePaie)) : this.convertDateToString(new Date())
    this.frequence = this.params.frequenceMensuelleId
    if (this.estDAS) {
      this.nombreVersements = 130
    }
    this.persistToStore()
  },
  methods: {
    ...mapActions(["setDonationInfo"]),
    setDefaultSelectedAmount() {
      if (this.defaultSelectedAmount) {
        this.amount = this.defaultSelectedAmount.amount
      }
    },
    changeDonationType(id) {
      this.error = ""
      this.donationTypeId = id
      this.amount = 0
      this.setDefaultSelectedAmount()
      this.persistToStore()
    },
    changeAmount(amount) {
      this.error = ""
      this.amount = amount
      this.persistToStore()
    },
    goNext() {
      this.$emit("to-step", 2)
    },
    persistToStore() {
      let donationInfo = {
        type: this.donationTypeId,
        amount: this.amount,
        wantsToReceiveRF: this.wantsToReceiveRF,
        projectId: this.project,
        frequence: this.frequence,
        nombreVersements: parseInt(this.nombreVersements),
        dateDebut: this.dateDebut,
        donFaitDeLaPartDe: this.donFaitDeLaPartDe,
        nomEnfant: this.nomEnfant,
        prenomEnfant: this.prenomEnfant,
        statutEnfant: this.statutEnfant,
        programmeEtude: this.programmeEtude,
        matriculeEnfant: this.matriculeEnfant,
        equipeVertEtOr: this.equipeVertEtOr
      }
      this.setDonationInfo(donationInfo)
    },
    validate() {
      if (this.$refs.form.validate()) {
        if (!this.amount || this.amount <= 0) {
          this.error = this.t("ERROR_MONTANT_POSITIF")
          return false
        }
        if (!this.donationTypeId) {
          this.error = this.t("ERROR_MUST_SELECT_TYPE")
          return false
        }

        this.persistToStore()
        this.$emit("validateStep", 0)
        this.goNext()
      }
    },
    frequenceChanged() {
      if (this.frequence == this.params.frequenceMensuelleId) {
        this.nombreVersements = 60
      } else {
        this.nombreVersements = 5
      }
      this.persistToStore()
    }
  }
}
</script>

<style>
#donation-form .v-stepper__step {
  padding: 0;
}
</style>